import { memo, useCallback, useMemo, useRef, useState } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { router, Stack, useLocalSearchParams } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, FormInput, Card, Button } from "@src/components";
import { Icon } from "@src/components/Icon";
import { SelectableUserList } from "@src/features/settings/components/SelectableUserList";
import { Section } from "@src/features/settings/components/Section";
import { PressableScale } from "pressto";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { AssignRolesBottomSheet } from "@src/features/settings/components/AssignRolesBottomSheet";
import { AssignRolesUserList, type AssignRole } from "@src/features/settings/components/AssignRolesUserList";
import { FloatingSelectionBar } from "@src/features/settings/components/FloatingSelectionBar";
import { CreateUserBottomSheet } from "@src/features/settings/components/CreateUserBottomSheet";
import { CreateUserForm } from "@src/features/settings/components/CreateUserForm";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import {
  useAccessManagement,
  useCreateRoleAssignment,
  useInviteUser,
  type AccessManagementRoleAssignment,
} from "@visioni/a3s-access/app";
import { useCameras } from "@src/features/cameras";
import { buildSiteAccessList } from "@src/features/settings/utils";
import type { UserWithSites } from "@src/features/settings/data/mock-all-users";
import { EditableRole } from "@visioni/a3s-access/schemas";
import { isWeb } from "@src/utils/web-helpers";
import { WebLayout } from "@src/components/web/WebLayout.web";
// @ts-expect-error baseline 2026-02-16
import { RightDrawer } from "@src/components/web";

const AddUsersToSiteScreen = memo(() => {
  const { siteId } = useLocalSearchParams<{ siteId: string }>();
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const createUserRef = useRef<BottomSheetModal>(null);

  const { data, isLoading } = useAccessManagement();
  const { data: cameras } = useCameras();
  const createRoleAssignment = useCreateRoleAssignment();
  const inviteUser = useInviteUser();

  const sites = useMemo(() => (data ? buildSiteAccessList(data, cameras) : []), [data, cameras]);
  const site = useMemo(() => sites.find((s) => s.id === siteId), [sites, siteId]);

  // Build all users with their site names from real data
  const allUsers: UserWithSites[] = useMemo(() => {
    const userMap = new Map<string, UserWithSites>();
    for (const s of sites) {
      for (const user of s.users) {
        const existing = userMap.get(user.id);
        if (existing) {
          if (!existing.siteNames.includes(s.name)) {
            existing.siteNames.push(s.name);
          }
        } else {
          userMap.set(user.id, {
            id: user.id,
            name: user.name,
            email: user.email,
            phoneNumber: user.phoneNumber,
            siteNames: [s.name],
          });
        }
      }
    }
    // Also include users from access management that have no site roles yet
    if (data) {
      for (const u of data.users) {
        const id = String(u.id);
        if (!userMap.has(id)) {
          userMap.set(id, {
            id,
            name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
            email: u.email,
            phoneNumber: u.phoneNumber ?? "",
            siteNames: [],
          });
        }
      }
    }
    return Array.from(userMap.values());
  }, [sites, data]);

  const [searchQuery, setSearchQuery] = useState("");

  const formattedSearchQuery = useMemo(
    () =>
      searchQuery
        .trim()
        .split(" ")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
        .join(" "),
    [searchQuery],
  );
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // Web-only: RightDrawer state for the "assign roles" step (on native the bottom sheet is used instead).
  const [isAssignRolesDrawerVisible, setIsAssignRolesDrawerVisible] = useState(false);
  const [webRoleMap, setWebRoleMap] = useState<Map<string, AssignRole>>(new Map());

  const handleWebRoleChange = useCallback((userId: string, role: AssignRole) => {
    setWebRoleMap((prev) => {
      const next = new Map(prev);
      next.set(userId, role);
      return next;
    });
  }, []);

  const handleCloseAssignRolesDrawer = useCallback(() => {
    setIsAssignRolesDrawerVisible(false);
  }, []);

  // Web-only: RightDrawer state for the "create user" step.
  const [isCreateUserDrawerVisible, setIsCreateUserDrawerVisible] = useState(false);

  const handleCloseCreateUserDrawer = useCallback(() => {
    setIsCreateUserDrawerVisible(false);
  }, []);

  const siteUserIds = useMemo(() => new Set(site?.users.map((u) => u.id)), [site]);

  const selectedUsers = useMemo(
    () => allUsers.filter((user) => selectedUserIds.has(user.id)),
    [allUsers, selectedUserIds],
  );

  const filteredUsers = useMemo(() => {
    const nonSiteUsers = allUsers.filter((user) => !siteUserIds.has(user.id));
    if (!searchQuery.trim()) return nonSiteUsers;
    const query = searchQuery.toLowerCase();
    return nonSiteUsers.filter(
      (user) => user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query),
    );
  }, [allUsers, searchQuery, siteUserIds]);

  const handleToggleUser = useCallback((userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const handleCreateNewUser = useCallback(() => {
    if (isWeb) {
      setIsCreateUserDrawerVisible(true);
      return;
    }
    createUserRef.current?.present();
  }, []);

  const handleSaveNewUser = useCallback(
    (formData: { email: string; role: string }) => {
      const role: EditableRole = formData.role === "manager" ? `site_manager:${siteId}` : `site_user:${siteId}`;
      inviteUser.mutate(
        { email: formData.email, role: role },
        {
          onSuccess: () => showSuccessToast({ message: "New user invited" }),
          onError: (error) =>
            showErrorToast({
              message:
                error.message.includes("does not have an existing account") ?
                  "This user doesn't have an account yet. Please contact support to create one."
                : "Failed to invite user",
            }),
        },
      );
    },
    [siteId, inviteUser],
  );

  const handleWebSaveNewUser = useCallback(
    (formData: { email: string; role: string }) => {
      handleSaveNewUser(formData);
      setIsCreateUserDrawerVisible(false);
    },
    [handleSaveNewUser],
  );

  const handleOpenAssignRolesToSelectedUsersBottomSheet = useCallback(() => {
    if (isWeb) {
      // Seed the role map with a default "viewer" role for every currently selected user.
      setWebRoleMap(new Map(selectedUsers.map((u) => [u.id, "viewer" as AssignRole])));
      setIsAssignRolesDrawerVisible(true);
      return;
    }
    bottomSheetRef.current?.present();
  }, [selectedUsers]);

  const handleSaveRoles = useCallback(
    async (assignments: { userId: string; role: "manager" | "viewer" }[]) => {
      try {
        await Promise.all(
          assignments.map((a) =>
            createRoleAssignment.mutateAsync({
              user: a.userId,
              role: a.role === "manager" ? `site_manager:${siteId}` : `site_user:${siteId}`,
            } as AccessManagementRoleAssignment),
          ),
        );
        bottomSheetRef.current?.dismiss();
        setTimeout(() => {
          showSuccessToast({ message: "New users added" });
          router.dismissTo(`/settings/site-access/${siteId}`);
        }, 300);
      } catch (error) {
        showErrorToast({ message: "Failed to add users" });
        throw error;
      }
    },
    [siteId, createRoleAssignment],
  );

  const handleWebSaveRoles = useCallback(async () => {
    const assignments = selectedUsers.map((user) => ({
      userId: user.id,
      role: webRoleMap.get(user.id) ?? ("viewer" as AssignRole),
    }));
    try {
      await handleSaveRoles(assignments);
    } catch {
      return;
    }
    setIsAssignRolesDrawerVisible(false);
  }, [selectedUsers, webRoleMap, handleSaveRoles]);

  if (isLoading) {
    if (isWeb) {
      return (
        <>
          <Stack.Screen options={{ headerShown: false }} />
          <WebLayout>
            <View style={styles.centered}>
              <ActivityIndicator />
            </View>
          </WebLayout>
        </>
      );
    }
    return (
      <>
        <Stack.Screen
          options={{
            title: "Add users",
            headerBackButtonDisplayMode: "minimal",
            headerStyle: { backgroundColor: theme.colors.background },
          }}
        />
        <View style={styles.centered}>
          <ActivityIndicator />
        </View>
      </>
    );
  }

  if (!site) {
    if (isWeb) {
      return (
        <>
          <Stack.Screen options={{ headerShown: false }} />
          <WebLayout>
            <View style={styles.centered}>
              <Icon name="errorOutline" size={theme.spacing["13xl"]} color={theme.colors.textMuted} />
              <Text textStyle="lg/regular" color="textMuted">
                Site not found
              </Text>
            </View>
          </WebLayout>
        </>
      );
    }
    return (
      <>
        <Stack.Screen
          options={{
            title: "Site not found",
            headerBackButtonDisplayMode: "minimal",
            headerStyle: { backgroundColor: theme.colors.background },
          }}
        />
        <View style={styles.centered}>
          <Icon
            name="errorOutline"
            size={theme.spacing["13xl"]}
            color={theme.colors.textMuted}
            style={{ marginBottom: theme.spacing["3xl"] }}
          />
          <Text textStyle="lg/regular" color="textMuted">
            Site not found
          </Text>
        </View>
      </>
    );
  }

  if (isWeb) {
    return (
      <>
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
        <WebLayout>
          <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
            <View gap="3xl" flexDirection="row" alignItems="flex-start">
              <Card style={{ flexBasis: "50%" }}>
                <View mb="xl" gap="2xl">
                  <Text textStyle="xs/regular" color="text">
                    Create new or search for existing users
                  </Text>
                  <FormInput
                    placeholder="Enter name or email"
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    iconLeft={<Icon name="search" color={theme.colors.textMuted} />}
                    clearable
                    size="medium"
                    containerStyle={styles.formInput}
                  />
                </View>
                {searchQuery.trim() ?
                  <>
                    <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="xl">
                      <Text textStyle="xs/regular" color="textMuted">
                        Results for &quot;{searchQuery.trim()}&quot;
                      </Text>
                      <Text textStyle="xs/regular" color="textMuted">
                        {filteredUsers.length} {filteredUsers.length === 1 ? "result" : "results"}
                      </Text>
                    </View>
                    <View style={styles.separator} />
                  </>
                : null}
                <PressableScale onPress={handleCreateNewUser} style={styles.pressable}>
                  <View style={styles.button}>
                    <Text textStyle="sm/regular">
                      {searchQuery.trim() ?
                        <>
                          Create &quot;<Text textStyle="sm/semibold">{formattedSearchQuery}</Text>&quot; as new user
                        </>
                      : "Create new user"}
                    </Text>
                    <Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.iconPrimary} />
                  </View>
                </PressableScale>
              </Card>
              <Card style={{ flexBasis: "50%" }}>
                <Section title="Existing users" titleTextVariant="xs/regular" titleColor="textMuted">
                  <SelectableUserList
                    users={filteredUsers}
                    selectedUserIds={selectedUserIds}
                    onToggleUser={handleToggleUser}
                  />
                </Section>
              </Card>
            </View>
          </ScrollView>
        </WebLayout>
        <FloatingSelectionBar
          selectedCount={selectedUserIds.size}
          actionLabel={`Add to ${site.name}`}
          onAction={handleOpenAssignRolesToSelectedUsersBottomSheet}
          bottomInset={bottom}
          style={styles.floatingSelectionBar}
        />
        <RightDrawer
          width={450}
          visible={isAssignRolesDrawerVisible}
          onClose={handleCloseAssignRolesDrawer}
          title={`Assign roles for ${site.name}`}
          footer={<Button variant="primary" label="Save" size="md" onPress={handleWebSaveRoles} />}
        >
          <AssignRolesUserList users={selectedUsers} roleMap={webRoleMap} onRoleChange={handleWebRoleChange} />
        </RightDrawer>
        <RightDrawer
          width={450}
          visible={isCreateUserDrawerVisible}
          onClose={handleCloseCreateUserDrawer}
          title={`Create new user for ${site.name}`}
        >
          <CreateUserForm initialEmail={searchQuery.trim()} onSave={handleWebSaveNewUser} />
        </RightDrawer>
      </>
    );
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: `Add users to ${site.name}`,
          headerBackButtonDisplayMode: "minimal",
          headerStyle: { backgroundColor: theme.colors.background },
        }}
      />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
        <View mb="7xl" gap="2xl">
          <Text textStyle="xs/regular" color="text">
            Create new or search for existing users
          </Text>
          <FormInput
            placeholder="Enter name or email"
            value={searchQuery}
            onChangeText={setSearchQuery}
            iconLeft={<Icon name="search" color={theme.colors.textMuted} />}
            clearable
            size="medium"
            containerStyle={styles.formInput}
          />
        </View>
        {searchQuery.trim() ?
          <>
            <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="xl">
              <Text textStyle="xs/regular" color="textMuted">
                Results for &quot;{searchQuery.trim()}&quot;
              </Text>
              <Text textStyle="xs/regular" color="textMuted">
                {filteredUsers.length} {filteredUsers.length === 1 ? "result" : "results"}
              </Text>
            </View>
            <View style={styles.separator} />
          </>
        : null}
        {searchQuery.trim() ?
          <SelectableUserList users={filteredUsers} selectedUserIds={selectedUserIds} onToggleUser={handleToggleUser} />
        : <Section title="Existing users" titleTextVariant="xs/regular" titleColor="textMuted">
            <SelectableUserList
              users={filteredUsers}
              selectedUserIds={selectedUserIds}
              onToggleUser={handleToggleUser}
            />
          </Section>
        }
        <PressableScale onPress={handleCreateNewUser} style={styles.pressable}>
          <View style={styles.button}>
            <Text textStyle="sm/regular">
              {searchQuery.trim() ?
                <>
                  Create &quot;<Text textStyle="sm/semibold">{formattedSearchQuery}</Text>&quot; as new user
                </>
              : "Create new user"}
            </Text>
            <Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.iconPrimary} />
          </View>
        </PressableScale>
      </ScrollView>
      <FloatingSelectionBar
        selectedCount={selectedUserIds.size}
        actionLabel={`Add to ${site.name}`}
        onAction={handleOpenAssignRolesToSelectedUsersBottomSheet}
        bottomInset={bottom}
      />
      <AssignRolesBottomSheet
        bottomSheetRef={bottomSheetRef}
        selectedUsers={selectedUsers}
        targetName={site.name}
        onSave={handleSaveRoles}
      />
      <CreateUserBottomSheet
        bottomSheetRef={createUserRef}
        siteName={site.name}
        initialSearchQuery={searchQuery.trim()}
        onSave={handleSaveNewUser}
      />
    </>
  );
});

export default AddUsersToSiteScreen;

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    padding: theme.spacing["3xl"],
    paddingBottom: theme.spacing["12xl"],
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  button: {
    height: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.interactive.secondary.default_border,
    backgroundColor: theme.colors.interactive.secondary.background_secondary,
  },
  formInput: {
    backgroundColor: theme.colors.surfaceCard,
    borderColor: theme.colors.field.border_default,
  },
  separator: {
    height: 1,
    backgroundColor: theme.colors.borderCard,
    marginBottom: theme.spacing["3xl"],
  },
  pressable: {
    marginTop: theme.spacing["3xl"],
  },
  floatingSelectionBar: {
    width: 350,
    left: "50%",
    right: "auto",
  },
}));
